import 'leaflet/dist/leaflet.css';

import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Alert,
  Backdrop,
  Box,
  Button,
  ButtonGroup,
  CircularProgress,
} from '@mui/material';
import {t} from 'i18next';
import update from 'immutability-helper';
import _, {isArray} from 'lodash';
import {useEffect, useMemo, useRef, useState} from 'react';

import API, {getMessagesFromApiError} from '../../../api/axios';
import {apiBaseUrl} from '../../../api/urls';
import {AssetStatus, CommunicationNodeStatus} from '../../../constants/status';
import {useAppSelector} from '../../../hooks/redux';
import {useRefreshInterval} from '../../../hooks/refreshInterval';
import {AlarmModuleNodeListResponse} from '../../../interfaces/AlarmModuleNode';
import {AlarmListQuery} from '../../../interfaces/AlarmType';
import {
  BeltMonitoringNodeListQuery,
  BeltMonitoringNodeListResponse,
} from '../../../interfaces/BeltMonitoringNode';
import {
  CommtracNodeListQuery,
  CommtracNodeListResponse,
} from '../../../interfaces/CommtracNode';
import {Company} from '../../../interfaces/Company';
import {
  DashboardEntity,
  DashboardPanelData,
} from '../../../interfaces/Dashboard';
import {
  AmsEventList,
  GasMonitoringNodeListQuery,
  GasMonitoringNodeListResponse,
} from '../../../interfaces/GasMonitoringNode';
import {NodeListQuery, NodeListResponse} from '../../../interfaces/Node';
import {getNodeKey} from '../../../utils/nodes';
import {AutoRefreshSelect} from '../../common/AutoRefreshSelect';
import {Map as MapComponent, MapLayerId} from '../../common/Map';
import {usePanel} from '../../dashboards/entities/DashboardEntityContext';
import {AlarmModuleStatusSelect} from '../../selectors/AlarmModuleStatusSelect';
import {AmsSensorStatusSelect} from '../../selectors/AmsSensorStatusSelect';
import {AssetsTypeStatusSelect} from '../../selectors/AssetsTypeStatusSelect';
import {BeltStatusSelect} from '../../selectors/BeltStatusSelect';
import {NodesTypeStatusSelect} from '../../selectors/NodesTypeStatusSelect';
import {ZonesSelect} from '../../selectors/ZonesSelect';
import {CnWifiNodestData} from '../CnWifiNodes';
import {DashboardPanelTitleSlot} from '../DashboardPanelTitleSlot';
import {EmployeesAssetsViewData} from '../EmployeeAssetsGrid';

export interface MapPanelData {
  params: Partial<CommtracNodeListQuery>;
  nodesParams: Partial<NodeListQuery>;
  amsParams: Partial<GasMonitoringNodeListQuery>;
  beltParams: Partial<BeltMonitoringNodeListQuery>;
  alarmModuleParams: Partial<AlarmListQuery>;
  refreshInterval?: number;
  activeId?: number;
  mapLayers: MapLayerId[];
  mapLevel: number | null;
  availableMapLayers: MapLayerId[];
}

export const getMapPanelData = (company: null | Company): MapPanelData => ({
  params: {
    miner_status: 'checked_in',
    asset_status: 'checked_in',
    node_type: ['asset', 'miner'],
    section_ids: [],
  },
  nodesParams: {
    communication_node_status: 'active',
    wifi_point_status: 'active',
    node_type: ['communication_node', 'wifi_point'],
    section_ids: [],
  },
  amsParams: {
    status: 'active',
    section_ids: [],
    map_level: null,
  },
  beltParams: {
    status: 'active',
    section_ids: [],
    map_level: null,
  },
  alarmModuleParams: {
    status: 'active',
    section_ids: [],
    map_level: null,
  },
  activeId: undefined,
  mapLayers: [
    'street',
    'mine',
    'nodes',
    'routing_network',
    'employees',
    'assets',
    ...(company?.ams_enabled ? ['ams_sensor_nodes' as const] : []),
    ...(company?.belt_enabled ? ['belts' as const] : []),
    ...(company?.alarm_enabled ? ['alarms' as const] : []),
  ],
  mapLevel: null,
  availableMapLayers: [
    'street',
    'mine',
    'nodes',
    'routing_network',
    'employees',
    'assets',
    ...(company?.ams_enabled ? ['ams_sensor_nodes' as const] : []),
    ...(company?.belt_enabled ? ['belts' as const] : []),
    ...(company?.alarm_enabled ? ['alarms' as const] : []),
  ],
});

interface Props {
  value?: DashboardPanelData;
  entities?: DashboardEntity[];
  onUpdate?: (value: DashboardPanelData) => void;
  onOpenHistory?: (
    id: number | string,
    type:
      | 'asset'
      | 'cn'
      | 'wifi'
      | 'wifiLongTerm'
      | 'employee'
      | 'commtracNodeByCn'
      | 'networkDiagnostics'
      | 'alarm'
      | 'alarm_log'
      | 'hazard_ai_detection_log'
      | 'hazard_ai_heatmap'
      | 'amsShortTerm'
      | 'amsLongTerm'
      | 'amsLocation'
      | 'amsEmoduleInstallationHistory'
      | 'amsEmoduleSensorHistory'
      | 'amsEmoduleCalibration'
      | 'beltLocationHistoryReport'
      | 'beltHistoryReport'
  ) => void;
}

export const Map = (props: Props) => {
  const [panel] = usePanel();
  const company = useAppSelector(({assets}) => assets.company);

  const employeeAseetsPanelData = props.entities?.find(
    (e) => e.name === 'Employees/Assets'
  )?.panel?.data as EmployeesAssetsViewData;

  const cnWifiPanelData = props.entities?.find(
    (e) => e.name === 'Communication/Wifi Nodes'
  )?.panel?.data as CnWifiNodestData;

  // Commtrac Nodes
  const [fetchedCommtracNodeData, setFetchedCommtracNodeData] =
    useState<CommtracNodeListResponse>();
  // Nodes
  const [fetchedNodesData, setFetchedNodesData] = useState<NodeListResponse>();
  // Alarm Modules
  const [fetchedAlarmModuleData, setFetchedAlarmModuleData] =
    useState<AlarmModuleNodeListResponse>();
  // Sensors
  const [fetchedGasMonitoringNodes, setFetchedGasMonitoringNodes] =
    useState<GasMonitoringNodeListResponse>();

  const amsMapData = useMemo(() => {
    if (fetchedGasMonitoringNodes?.items) {
      const commtrac_external_ids: number[] = [];
      const channelEvents: {
        [key: number]: {
          [key: number]: AmsEventList;
        };
      } = {};
      return fetchedGasMonitoringNodes?.items.filter((i) => {
        if (!commtrac_external_ids.includes(i.commtrac_external_id)) {
          commtrac_external_ids.push(i.commtrac_external_id);
          i.channel_events = {};
          channelEvents[i.commtrac_external_id] = i.channel_events;
          if (i.emodule_id) {
            i.channel_events[i.emodule_id] = i.events;
          }
          return true;
        }

        if (i.emodule_id) {
          channelEvents[i.commtrac_external_id][i.emodule_id] = i.events;
        }
      });
    }
    return [];
  }, [fetchedGasMonitoringNodes]);

  // Belts
  const [fetchedBeltMonitoringNodes, setFetchedBeltMonitoringNodes] =
    useState<BeltMonitoringNodeListResponse>();

  const [isFetching, setIsFetching] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);

  const lastMapLevelRef = useRef<number | null>(null);

  const employeeAseetsParams = employeeAseetsPanelData?.commtracNodes?.params;
  const employeeAseetsSelectedNodeIds =
    employeeAseetsPanelData?.commtracNodes?.selectedIds;

  const cnWifiParams = cnWifiPanelData?.nodes?.params;
  const cnWifiSelectedNodeIds = cnWifiPanelData?.nodes?.selectedIds;

  const [config, setConfig] = useState<MapPanelData>(
    !_.isEmpty(props.value)
      ? (props.value as unknown as MapPanelData)
      : getMapPanelData(company)
  );

  const selectedCommtracNodes = useMemo(() => {
    if (!employeeAseetsSelectedNodeIds) {
      return fetchedCommtracNodeData?.items;
    }
    return (
      fetchedCommtracNodeData?.items.filter((n) =>
        employeeAseetsSelectedNodeIds?.includes(n.id)
      ) ?? []
    );
  }, [employeeAseetsSelectedNodeIds, fetchedCommtracNodeData]);

  const selectedNodes = useMemo(() => {
    if (!cnWifiSelectedNodeIds) {
      return fetchedNodesData?.items;
    }
    return (
      fetchedNodesData?.items.filter((n) =>
        cnWifiSelectedNodeIds?.includes(getNodeKey(n))
      ) ?? []
    );
  }, [cnWifiSelectedNodeIds, fetchedNodesData]);

  const fetchData = async () => {
    setIsFetching(true);
    setErrors([]);

    try {
      const standby =
        !!employeeAseetsPanelData &&
        employeeAseetsPanelData.viewType !== 'commtracNodes';

      if (
        config?.mapLayers?.includes('employees') ||
        config?.mapLayers?.includes('assets')
      ) {
        const resp = await API.get<CommtracNodeListResponse>(
          `${apiBaseUrl}/commtrac-node`,
          {
            params: {
              ...config.params,
              standby,
              map_level: config.params.map_level,
              limit: 5000,
            },
          }
        );
        setFetchedCommtracNodeData(resp.data);
      }
      if (config?.mapLayers?.includes('nodes')) {
        const resp = await API.get<NodeListResponse>(`${apiBaseUrl}/node`, {
          params: {
            ...config.nodesParams,
            standby,
            map_level: config.params.map_level,
            limit: 5000,
          },
        });
        setFetchedNodesData(resp.data ?? []);
      }
      if (company?.alarm_enabled && config?.mapLayers?.includes('alarms')) {
        const resp = await API.get<AlarmModuleNodeListResponse>(
          `${apiBaseUrl}/alarm-module`,
          {
            params: {
              ...config.alarmModuleParams,
              section_ids: config.params.section_ids,
              map_level: config.params.map_level,
              standby,
              limit: 5000,
            },
          }
        );
        setFetchedAlarmModuleData(resp.data ?? []);
      }
      if (company?.belt_enabled && config?.mapLayers?.includes('belts')) {
        const resp = await API.get<BeltMonitoringNodeListResponse>(
          `${apiBaseUrl}/belt`,
          {
            params: {
              ...config.beltParams,
              section_ids: config.params.section_ids,
              map_level: config.params.map_level,
              limit: 10000,
            },
          }
        );
        setFetchedBeltMonitoringNodes(resp.data);
      }
      if (
        company?.ams_enabled &&
        config?.mapLayers?.includes('ams_sensor_nodes')
      ) {
        const resp = await API.get<GasMonitoringNodeListResponse>(
          `${apiBaseUrl}/ams`,
          {
            params: {
              ...config.amsParams,
              section_ids: config.params.section_ids,
              map_level: config.params.map_level,
              limit: 10000,
            },
          }
        );

        setFetchedGasMonitoringNodes(resp.data);
      }
    } catch (e: any) {
      const messages = getMessagesFromApiError(e, 'STRA_a425ca19');
      setErrors(messages);
    }

    setIsFetching(false);
  };

  const [refreshInterval, setRefreshInterval] = useRefreshInterval(
    fetchData,
    props.value?.refreshInterval ?? 30000
  );

  useEffect(() => {
    if (!isFetching) {
      fetchData();
    }
  }, [
    config.params,
    config.nodesParams,
    config.alarmModuleParams,
    config.amsParams,
    config.beltParams,
    config.mapLayers,
  ]);

  useEffect(() => {
    // If dashboard contains employee/asset panel we should
    // override filter values with employee/asset filters
    if (employeeAseetsParams) {
      setConfig({...config, params: employeeAseetsParams});
    }
    if (cnWifiParams) {
      setConfig({...config, nodesParams: cnWifiParams});
    }
  }, [employeeAseetsParams, cnWifiParams]);

  useEffect(() => {
    props.onUpdate?.({...props.value, ...config, refreshInterval});
  }, [config, refreshInterval]);

  useEffect(() => {
    if (lastMapLevelRef.current === config.mapLevel) {
      return;
    }
    lastMapLevelRef.current = config.mapLevel;
    setConfig(
      update(config, {
        params: {
          section_ids: {
            $set: [],
          },
          map_level: {
            $set: config.mapLevel,
          },
        },
        nodesParams: {
          section_ids: {
            $set: [],
          },
          map_level: {
            $set: config.mapLevel,
          },
        },
      })
    );
  }, [config.mapLevel]);

  const countByLayerData = useMemo(() => {
    return [
      ...(fetchedNodesData?.count_by_layer ?? []),
      ...(fetchedCommtracNodeData?.count_by_layer ?? []),
      ...(fetchedAlarmModuleData?.count_by_layer ?? []),
      ...(fetchedGasMonitoringNodes?.count_by_layer ?? []),
      ...(fetchedBeltMonitoringNodes?.count_by_layer ?? []),
    ];
  }, [
    fetchedNodesData,
    fetchedCommtracNodeData,
    fetchedAlarmModuleData,
    fetchedGasMonitoringNodes,
    fetchedBeltMonitoringNodes,
  ]);

  return (
    <Box
      display="flex"
      flexDirection="column"
      height="100%"
      width="100%"
      overflow="hidden"
    >
      <DashboardPanelTitleSlot>
        {t(`panels.${panel?.code}`)}
      </DashboardPanelTitleSlot>
      <Backdrop open={isFetching} sx={{position: 'absolute', zIndex: 1001}}>
        <CircularProgress color="inherit" />
      </Backdrop>
      <Box display="flex" justifyContent="space-between" gap={1} py={1}>
        <Box
          display="flex"
          height="100%"
          width="100%"
          gap={1}
          visibility={!employeeAseetsPanelData ? 'visible' : 'hidden'}
        >
          <NodesTypeStatusSelect
            fullWidth
            size="small"
            value={[
              config.nodesParams?.node_type &&
              config.nodesParams.node_type.length === 1
                ? config.nodesParams.node_type[0]
                : 'typeAll',
              config.nodesParams?.communication_node_status as string,
            ]}
            onChange={(v) => {
              const type = (v as string[])?.[0] ?? 'typeAll';
              const status = (v as string[])?.[1] ?? 'active';
              const types =
                type === 'typeAll'
                  ? ['communication_node', 'wifi_point']
                  : [type];
              setConfig(
                update(config, {
                  nodesParams: {
                    node_type: {
                      $set: types as ('communication_node' | 'wifi_point')[],
                    },
                    communication_node_status: {
                      $set: status as CommunicationNodeStatus,
                    },
                    wifi_point_status: {
                      $set: status as CommunicationNodeStatus,
                    },
                  },
                })
              );
            }}
          />
          <AssetsTypeStatusSelect
            fullWidth
            size="small"
            value={[
              config.params?.node_type && config.params.node_type.length === 1
                ? config.params.node_type[0]
                : 'typeAll',
              config.params?.asset_status as string,
            ]}
            onChange={(v) => {
              const type = (v as string[])?.[0] ?? 'typeAll';
              const status = (v as string[])?.[1] ?? 'checked_in';
              const types = type === 'typeAll' ? ['asset', 'miner'] : [type];
              setConfig(
                update(config, {
                  params: {
                    node_type: {
                      $set: types as ('asset' | 'miner')[],
                    },
                    asset_status: {
                      $set: status as AssetStatus,
                    },
                    miner_status: {
                      $set: status as AssetStatus,
                    },
                  },
                })
              );
            }}
          />
          {company?.ams_enabled && (
            <AmsSensorStatusSelect
              value={config.amsParams?.status ?? 'active'}
              fullWidth
              size="small"
              onChange={(v) => {
                setConfig(
                  update(config, {
                    amsParams: {
                      status: {
                        $set: v as
                          | 'all'
                          | 'active'
                          | 'inactive'
                          | 'unacknowledged',
                      },
                    },
                  })
                );
              }}
            />
          )}
          {company?.alarm_enabled && (
            <AlarmModuleStatusSelect
              value={config.alarmModuleParams?.status ?? 'active'}
              fullWidth
              size="small"
              onChange={(v) => {
                setConfig(
                  update(config, {
                    alarmModuleParams: {
                      status: {
                        $set: v as
                          | 'all'
                          | 'active'
                          | 'inactive'
                          | 'unacknowledged',
                      },
                    },
                  })
                );
              }}
            />
          )}
          {company?.belt_enabled && (
            <BeltStatusSelect
              value={config.beltParams?.status ?? 'active'}
              fullWidth
              size="small"
              onChange={(v) => {
                setConfig(
                  update(config, {
                    beltParams: {
                      status: {
                        $set: v as
                          | 'all'
                          | 'active'
                          | 'inactive'
                          | 'unacknowledged',
                      },
                    },
                  })
                );
              }}
            />
          )}
          <ZonesSelect
            size="small"
            fullWidth
            nullLabel="All Sections"
            value={config.params.section_ids}
            multiple={true}
            layer={config.mapLevel}
            maxDefaultWidth={367}
            onChange={(v) => {
              const sectionIds = isArray(v) ? v : [+v];
              setConfig(
                update(config, {
                  params: {
                    section_ids: {
                      $set: sectionIds as number[],
                    },
                  },
                  nodesParams: {
                    section_ids: {
                      $set: sectionIds as number[],
                    },
                  },
                })
              );
            }}
          />
        </Box>

        <Box display="flex" height="100%">
          <ButtonGroup>
            <Button size="small" onClick={fetchData}>
              <RefreshIcon />
            </Button>
            <AutoRefreshSelect
              value={refreshInterval ?? null}
              onChange={setRefreshInterval}
            />
          </ButtonGroup>
        </Box>
      </Box>
      {errors.map((error, index) => (
        <Alert key={index} severity="error" sx={{mb: 2}}>
          {error}
        </Alert>
      ))}

      <MapComponent
        selectedLevel={config.mapLevel}
        selectedMapLayers={config.mapLayers}
        availableMapLayers={config.availableMapLayers}
        nodes={selectedNodes}
        alarmNodes={fetchedAlarmModuleData?.items}
        amsSensorNodes={amsMapData}
        beltsNodes={fetchedBeltMonitoringNodes?.items}
        commtracNodes={selectedCommtracNodes}
        countByLayer={countByLayerData}
        onSelectMapLayers={(v) => {
          setConfig({
            ...config,
            mapLayers: v,
          });
        }}
        onSelectLevel={(v) => {
          setConfig({
            ...config,
            mapLevel: v,
          });
        }}
        onOpenHistory={props.onOpenHistory}
        disableEditMode={true}
      />
    </Box>
  );
};
